/**
 * PlaceBindingStep - Step de vínculos filtrado por local.
 *
 * Substitui ProfessionalServiceMapStep. O WizardShell expande este step em N instancias
 * (uma por local selecionado), cada uma recebendo filterPlaceId via stepProps.
 *
 * Para cada profissional selecionado, exibe checkboxes de serviços.
 * Auto-gera links (place-professional, place-service, place-service-professional).
 */

import { useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, Checkbox, FormControlLabel, FormGroup,
  Paper, Divider, Alert,
} from '@mui/material';

export default function PlaceBindingStep({
  entityTree,
  stepProps = {},
  onValidChange,
}) {
  const { tree } = entityTree;
  const { filterPlaceId } = stepProps;

  const place = useMemo(
    () => tree.places.find(p => p.id === filterPlaceId),
    [tree.places, filterPlaceId]
  );

  const selectedProfessionals = useMemo(
    () => tree.professionals.filter(p => p.selected),
    [tree.professionals]
  );

  const selectedServices = useMemo(
    () => tree.services.filter(s => s.selected),
    [tree.services]
  );

  // Validação: pelo menos 1 vínculo neste local
  useEffect(() => {
    if (!filterPlaceId) {
      onValidChange(false, 'Local não identificado');
      return;
    }
    const hasLinks = tree.links.placeServiceProfessionals.some(
      l => l.place === filterPlaceId
    );

    let validationMessage = '';
    const isValid = hasLinks || (selectedProfessionals.length > 0 && selectedServices.length > 0);

    if (!isValid) {
      if (selectedProfessionals.length === 0) {
        validationMessage = 'Adicione profissionais antes de continuar';
      } else if (selectedServices.length === 0) {
        validationMessage = 'Adicione serviços antes de continuar';
      } else {
        validationMessage = 'Selecione quais serviços cada profissional faz';
      }
    }

    onValidChange(isValid, validationMessage);
  }, [tree.links.placeServiceProfessionals, filterPlaceId, selectedProfessionals, selectedServices, onValidChange]);

  // Auto-gerar links para este local
  useEffect(() => {
    if (!filterPlaceId) return;

    for (const prof of selectedProfessionals) {
      entityTree.linkProfessionalToPlace(filterPlaceId, prof.id);
      for (const svc of selectedServices) {
        entityTree.linkProfessionalToServiceAtPlace(filterPlaceId, svc.id, prof.id);
      }
    }
    for (const svc of selectedServices) {
      entityTree.linkServiceToPlace(filterPlaceId, svc.id);
    }
  }, [filterPlaceId, selectedProfessionals.length, selectedServices.length]);

  const isLinked = (serviceId, profId) => {
    return tree.links.placeServiceProfessionals.some(
      l => l.place === filterPlaceId && l.service === serviceId && l.professional === profId
    );
  };

  const handleToggleLink = (serviceId, profId) => {
    if (isLinked(serviceId, profId)) {
      entityTree.setTree(prev => ({
        ...prev,
        links: {
          ...prev.links,
          placeServiceProfessionals: prev.links.placeServiceProfessionals.filter(
            l => !(l.place === filterPlaceId && l.service === serviceId && l.professional === profId)
          ),
        },
      }));
    } else {
      entityTree.linkProfessionalToServiceAtPlace(filterPlaceId, serviceId, profId);
    }
  };

  const handleToggleAll = (profId) => {
    const allLinked = selectedServices.every(svc => isLinked(svc.id, profId));
    if (allLinked) {
      entityTree.setTree(prev => ({
        ...prev,
        links: {
          ...prev.links,
          placeServiceProfessionals: prev.links.placeServiceProfessionals.filter(
            l => !(l.place === filterPlaceId && l.professional === profId)
          ),
        },
      }));
    } else {
      for (const svc of selectedServices) {
        entityTree.linkProfessionalToServiceAtPlace(filterPlaceId, svc.id, profId);
      }
    }
  };

  if (!place) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="warning">
          Local não encontrado. Volte e selecione um local.
        </Alert>
      </Box>
    );
  }

  if (selectedProfessionals.length === 0 || selectedServices.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="warning">
          Você precisa ter pelo menos 1 profissional e 1 serviço selecionados para configurar os vínculos.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          Quais serviços cada profissional oferecerá em <strong>{place.name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecione os serviços que cada profissional poderá executar neste local. Você poderá fazer ajustes depois.
        </Typography>
      </Box>

      {selectedProfessionals.map(prof => {
        const allLinked = selectedServices.every(svc => isLinked(svc.id, prof.id));
        const someLinked = selectedServices.some(svc => isLinked(svc.id, prof.id));

        return (
          <Paper key={prof.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {prof.name} prestará os serviços de:
                {prof.isNew && <Chip size="small" label="Novo" color="primary" variant="outlined" sx={{ ml: 1 }} />}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allLinked}
                    indeterminate={someLinked && !allLinked}
                    onChange={() => handleToggleAll(prof.id)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Todos</Typography>}
              />
            </Box>

            <Divider sx={{ mb: 1 }} />

            <FormGroup row>
              {selectedServices.map(svc => (
                <FormControlLabel
                  key={svc.id}
                  control={
                    <Checkbox
                      checked={isLinked(svc.id, prof.id)}
                      onChange={() => handleToggleLink(svc.id, prof.id)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{svc.name}</span>
                      {svc.isNew && <Chip size="small" label="Novo" color="secondary" variant="outlined" />}
                    </Box>
                  }
                  sx={{ mr: 3 }}
                />
              ))}
            </FormGroup>
          </Paper>
        );
      })}

      <Alert severity="info" sx={{ mt: 2 }}>
        Você pode ajustar esses vínculos depois nas configurações do local.
      </Alert>
    </Box>
  );
}
